import React, { useCallback, useMemo, useState } from "react";
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    BarChart,
    Line,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    LabelList,
} from "recharts";
import { GameStatsItem, GameVipStatsData } from "../api/financial";

type TabId =
    | "overview"
    | "month"
    | "cohorts"
    | "retention"
    | "distribution"
    | "shop";

const TABS: { id: TabId; label: string }[] = [
    { id: "overview", label: "Обзор" },
    { id: "month", label: "Текущий месяц" },
    { id: "shop", label: "Магазин" },
    { id: "cohorts", label: "Когорты" },
    { id: "retention", label: "Удержание" },
    { id: "distribution", label: "Продления" },
];

/**
 * Analytics Dashboard palette (ui-ux-pro-max --design-system)
 * Primary #1E40AF · Secondary #3B82F6 · Accent #D97706 · success green
 */
const SERIES = {
    regs: "#3B82F6",
    regsFill: "rgba(59, 130, 246, 0.18)",
    vipActive: "#059669",
    vipDropped: "#DC2626",
    firstVip: "#1E40AF",
    conversion: "#D97706",
    renewal: "#1E40AF",
    structureVip: "#047857",
    structurePay: "#7C3AED",
    grid: "rgba(148, 163, 184, 0.28)",
    tick: "#64748B",
} as const;

function fmt(n: number | null | undefined): string {
    if (n === null || n === undefined) return "—";
    return n.toLocaleString("ru-RU");
}

function pct(n: number | null | undefined): string {
    if (n === null || n === undefined) return "—";
    return `${n.toLocaleString("ru-RU", { maximumFractionDigits: 2 })}%`;
}

/** Тултіп біля курсору — координати відносно контейнера графіка */
function useCursorTooltipPosition() {
    const [position, setPosition] = useState<
        { x: number; y: number } | undefined
    >();

    const onMouseMove = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setPosition({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            });
        },
        []
    );

    const onMouseLeave = useCallback(() => {
        setPosition(undefined);
    }, []);

    return { position, onMouseMove, onMouseLeave };
}

type TooltipEntry = {
    name?: string | number;
    value?: number | string | ReadonlyArray<number | string>;
    dataKey?: string | number;
    color?: string;
};

/** ui-ux-pro-max: tooltip-on-interact — точні значення біля курсору */
function ChartTooltipContent({
    active,
    payload,
    label,
}: {
    active?: boolean;
    payload?: ReadonlyArray<TooltipEntry>;
    label?: string | number;
}) {
    if (!active || !payload?.length) return null;

    return (
        <div
            className="pointer-events-none min-w-[168px] rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-md dark:border-slate-600 dark:bg-slate-900"
            role="tooltip"
        >
            {label != null && label !== "" ? (
                <div className="mb-1.5 border-b border-slate-100 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:text-slate-400">
                    {String(label)}
                </div>
            ) : null}
            <ul className="space-y-1">
                {payload.map((entry: TooltipEntry, i: number) => {
                    const raw = entry.value;
                    const num =
                        typeof raw === "number" ? raw : Number(raw);
                    const isPct =
                        String(entry.name).includes("%") ||
                        String(entry.dataKey) === "conversion";
                    const display = Number.isFinite(num)
                        ? isPct
                            ? pct(num)
                            : fmt(num)
                        : "—";
                    return (
                        <li
                            key={`${String(entry.dataKey ?? entry.name)}-${i}`}
                            className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200"
                        >
                            <span
                                className="inline-block h-2 w-2 shrink-0 rounded-full"
                                style={{
                                    backgroundColor: entry.color ?? "#94a3b8",
                                }}
                                aria-hidden
                            />
                            <span className="flex-1 truncate text-slate-500 dark:text-slate-400">
                                {String(entry.name ?? entry.dataKey)}
                            </span>
                            <span className="font-semibold tabular-nums text-slate-900 dark:text-slate-50">
                                {display}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

const chartTooltipSharedProps = {
    content: ChartTooltipContent,
    offset: 12,
    allowEscapeViewBox: { x: true, y: true } as const,
    wrapperStyle: { outline: "none", zIndex: 50 },
    cursor: { fill: "rgba(30, 64, 175, 0.06)" },
    isAnimationActive: false as const,
    shared: true,
};

type CohortRow = GameStatsItem & {
    conversion: number;
    vip_active: number;
    vip_dropped: number;
    first_vip_same_month: number;
};

const SeriesChip: React.FC<{
    color: string;
    label: string;
    checked: boolean;
    onChange: (v: boolean) => void;
    dashed?: boolean;
}> = ({ color, label, checked, onChange, dashed }) => (
    <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700 transition-colors hover:border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-slate-500">
        <input
            type="checkbox"
            className="sr-only"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
        />
        <span
            className={`h-2 w-3 shrink-0 rounded-sm ${checked ? "opacity-100" : "opacity-30"}`}
            style={{
                backgroundColor: dashed ? "transparent" : color,
                borderTop: dashed ? `2px dashed ${color}` : undefined,
            }}
            aria-hidden
        />
        <span className={checked ? "font-medium" : "text-slate-400 line-through"}>
            {label}
        </span>
    </label>
);

interface Props {
    data?: GameVipStatsData;
    /** когорты, если полная статистика ещё не пришла */
    fallbackCohorts?: GameStatsItem[];
    isLoading?: boolean;
    isError?: boolean;
    onRetry?: () => void;
}

const MetricCard: React.FC<{
    title: string;
    value: string;
    hint?: string;
    tone?: "default" | "green" | "blue" | "orange" | "purple";
}> = ({ title, value, hint, tone = "default" }) => {
    const tones: Record<string, string> = {
        default:
            "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40",
        green: "border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20",
        blue: "border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20",
        orange:
            "border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20",
        purple:
            "border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20",
    };
    const valueTones: Record<string, string> = {
        default: "text-gray-900 dark:text-gray-100",
        green: "text-green-700 dark:text-green-300",
        blue: "text-blue-700 dark:text-blue-300",
        orange: "text-orange-700 dark:text-orange-300",
        purple: "text-purple-700 dark:text-purple-300",
    };

    return (
        <div className={`rounded-md border p-4 ${tones[tone]}`}>
            <div className="text-sm text-gray-500 dark:text-gray-400">{title}</div>
            <div className={`mt-1 text-2xl font-bold ${valueTones[tone]}`}>
                {value}
            </div>
            {hint ? (
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {hint}
                </div>
            ) : null}
        </div>
    );
};

export const GameVipStatsPanel: React.FC<Props> = ({
    data,
    fallbackCohorts = [],
    isLoading,
    isError,
    onRetry,
}) => {
    const [tab, setTab] = useState<TabId>("overview");
    const [monthsWindow, setMonthsWindow] = useState<3 | 6 | 12>(12);
    const [hideEmptyMonths, setHideEmptyMonths] = useState(true);
    const [showRegs, setShowRegs] = useState(true);
    const [showVipActive, setShowVipActive] = useState(true);
    const [showVipDropped, setShowVipDropped] = useState(false);
    const [showFirstVip, setShowFirstVip] = useState(false);
    const [showConversion, setShowConversion] = useState(true);

    const cohortsSource: GameStatsItem[] =
        data?.monthly_cohorts?.length
            ? data.monthly_cohorts
            : fallbackCohorts;

    const filteredCohorts = useMemo(() => {
        let rows = [...cohortsSource];
        if (monthsWindow < 12) {
            rows = rows.slice(-monthsWindow);
        }
        if (hideEmptyMonths) {
            rows = rows.filter(
                (r) =>
                    (r.registrations || 0) > 0 ||
                    (r.vip_active || 0) > 0 ||
                    (r.bought_vip_ever || 0) > 0 ||
                    (r.first_vip_same_month || 0) > 0
            );
        }
        return rows.map((c) => ({
            ...c,
            conversion: c.conversion_pct ?? 0,
            vip_active: c.vip_active ?? 0,
            vip_dropped: c.vip_dropped ?? 0,
            first_vip_same_month: c.first_vip_same_month ?? 0,
            bought_vip_ever: c.bought_vip_ever ?? 0,
        }));
    }, [cohortsSource, monthsWindow, hideEmptyMonths]);

    const renewalChart = useMemo(() => {
        if (!data?.renewal_distribution) return [];
        return [
            { name: "1 оплата", users: data.renewal_distribution.payments_1 },
            { name: "2 оплаты", users: data.renewal_distribution.payments_2 },
            {
                name: "3+ оплат",
                users: data.renewal_distribution.payments_3_plus,
            },
        ];
    }, [data?.renewal_distribution]);

    const hasFullData = Boolean(data?.overview);

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 space-y-5">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                        VIP — структура и игра
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-3xl">
                        Сверху — все активные VIP в структуре. Когорты и вкладки
                        ниже — отдельно по игрокам, пришедшим из игры: «VIP
                        сейчас» в когорте = зарегистрировался в этом месяце и
                        сейчас платный VIP, а не общее число VIP.
                    </p>
                </div>
                <div className="text-xs text-gray-400 shrink-0">
                    {isLoading
                        ? "Загрузка…"
                        : data?.generated_at
                          ? `Обновлено: ${new Date(
                                data.generated_at
                            ).toLocaleString("ru-RU")}`
                          : null}
                </div>
            </div>

            {/* Фильтры */}
            <div className="flex flex-col gap-3 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 p-3">
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    График когорт — период и серии
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <label className="text-sm text-gray-700 dark:text-gray-200 flex items-center gap-2">
                        Окно
                        <select
                            className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm"
                            value={monthsWindow}
                            onChange={(e) =>
                                setMonthsWindow(
                                    Number(e.target.value) as 3 | 6 | 12
                                )
                            }
                        >
                            <option value={3}>3 мес.</option>
                            <option value={6}>6 мес.</option>
                            <option value={12}>12 мес.</option>
                        </select>
                    </label>
                    <label className="text-sm text-gray-700 dark:text-gray-200 flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={hideEmptyMonths}
                            onChange={(e) =>
                                setHideEmptyMonths(e.target.checked)
                            }
                        />
                        Скрыть пустые месяцы
                    </label>
                </div>
                {(tab === "cohorts" || tab === "overview" || !hasFullData) && (
                    <div className="flex flex-wrap gap-2">
                        <SeriesChip
                            color={SERIES.regs}
                            label="Регистрации"
                            checked={showRegs}
                            onChange={setShowRegs}
                        />
                        <SeriesChip
                            color={SERIES.vipActive}
                            label="VIP сейчас (из когорты)"
                            checked={showVipActive}
                            onChange={setShowVipActive}
                        />
                        <SeriesChip
                            color={SERIES.vipDropped}
                            label="VIP просрочен (из когорты)"
                            checked={showVipDropped}
                            onChange={setShowVipDropped}
                        />
                        <SeriesChip
                            color={SERIES.firstVip}
                            label="VIP в месяц регистрации"
                            checked={showFirstVip}
                            onChange={setShowFirstVip}
                        />
                        <SeriesChip
                            color={SERIES.conversion}
                            label="Конверсия %"
                            checked={showConversion}
                            onChange={setShowConversion}
                            dashed
                        />
                    </div>
                )}
            </div>

            {/* Табы */}
            <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-700 pb-3">
                {TABS.map((t) => {
                    const locked =
                        !hasFullData &&
                        t.id !== "cohorts" &&
                        t.id !== "overview";
                    return (
                        <button
                            key={t.id}
                            type="button"
                            disabled={locked}
                            title={
                                locked
                                    ? "Доступно после загрузки полной статистики"
                                    : undefined
                            }
                            onClick={() => setTab(t.id)}
                            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                                tab === t.id
                                    ? "bg-blue-600 text-white"
                                    : locked
                                      ? "bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed"
                                      : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                            }`}
                        >
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {!hasFullData && !isLoading ? (
                <div className="rounded-md border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-900 dark:text-amber-100 space-y-2">
                    <p className="font-medium">
                        Полная VIP-статистика не загрузилась — часть вкладок
                        временно недоступна.
                    </p>
                    <p className="text-amber-800 dark:text-amber-200/90">
                        Сейчас показаны только когорты по упрощённым данным.
                        Обновите страницу или нажмите «Повторить».
                        {isError
                            ? " Запрос полной статистики не удался."
                            : ""}
                    </p>
                    {onRetry ? (
                        <button
                            type="button"
                            onClick={onRetry}
                            className="inline-flex items-center rounded bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
                        >
                            Повторить
                        </button>
                    ) : null}
                </div>
            ) : null}

            {isLoading && !hasFullData && (
                <div className="text-sm text-gray-500">Загрузка…</div>
            )}

            {tab === "overview" && (
                <div className="space-y-4">
                    {hasFullData && data?.structure_overview ? (
                        <div className="space-y-3">
                            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                                VIP в структуре сейчас
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 -mt-1">
                                Все активные VIP в реферальной структуре (в
                                дереве, доступ открыт, не заблокированы) — не
                                только игроки.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                <MetricCard
                                    title="Активный VIP"
                                    value={fmt(
                                        data.structure_overview
                                            .effective_vip_active
                                    )}
                                    hint="платный или подарочный"
                                    tone="green"
                                />
                                <MetricCard
                                    title="Платный VIP"
                                    value={fmt(
                                        data.structure_overview.paid_vip_active
                                    )}
                                    hint="срок оплаты ещё действует"
                                    tone="green"
                                />
                                <MetricCard
                                    title="из них из игры"
                                    value={fmt(
                                        data.structure_overview
                                            .paid_vip_from_game
                                    )}
                                    tone="blue"
                                />
                                <MetricCard
                                    title="платный не из игры"
                                    value={fmt(
                                        data.structure_overview
                                            .paid_vip_not_from_game
                                    )}
                                    hint="кабинет / структура без игры"
                                    tone="orange"
                                />
                                <MetricCard
                                    title="Подарочный VIP"
                                    value={fmt(
                                        data.structure_overview
                                            .complimentary_vip_active
                                    )}
                                    hint="без оплаты"
                                />
                                <MetricCard
                                    title="В структуре (всего)"
                                    value={fmt(
                                        data.structure_overview
                                            .in_structure_total
                                    )}
                                    hint="с VIP и без"
                                    tone="purple"
                                />
                            </div>
                        </div>
                    ) : null}

                    {hasFullData &&
                        data?.monthly_structure &&
                        data.monthly_structure.length > 0 && (
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    По месяцам: сколько VIP было покрыто до
                                    конца месяца и сколько платежей
                                </h3>
                                <StructureMonthlyChart
                                    data={data.monthly_structure}
                                />
                            </div>
                        )}

                    {hasFullData && data ? (
                        <div className="space-y-3">
                            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                                Срез по игрокам (из игры)
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            <MetricCard
                                title="Пользователи из игры"
                                value={fmt(data.overview.game_users_total)}
                                tone="blue"
                            />
                            <MetricCard
                                title="VIP активны (платные, игра)"
                                value={fmt(data.overview.paid_vip_active)}
                                hint="игроки со сроком оплаты"
                                tone="green"
                            />
                            <MetricCard
                                title="Продлевают (≥2 оплаты)"
                                value={fmt(data.overview.active_renewers)}
                                tone="purple"
                            />
                            <MetricCard
                                title="Только 1-й период"
                                value={fmt(
                                    data.overview.active_first_period_only
                                )}
                                tone="orange"
                            />
                            <MetricCard
                                title="Был VIP, сейчас нет"
                                value={fmt(
                                    data.overview.paid_vip_expired_or_inactive
                                )}
                            />
                            <MetricCard
                                title="Когда-либо покупали VIP"
                                value={fmt(
                                    data.overview.paid_vip_ever_marker
                                )}
                            />
                            <MetricCard
                                title="Подарочный VIP активен"
                                value={fmt(
                                    data.overview.complimentary_vip_active
                                )}
                                hint="без оплаты"
                            />
                            <MetricCard
                                title="До первого VIP (медиана)"
                                value={
                                    data.time_to_first_vip.median_days !== null
                                        ? `${data.time_to_first_vip.median_days} дн.`
                                        : "—"
                                }
                                hint={
                                    data.time_to_first_vip.avg_days !== null
                                        ? `среднее ${data.time_to_first_vip.avg_days} дн., выборка ${data.time_to_first_vip.sample_size}`
                                        : undefined
                                }
                                tone="blue"
                            />
                            {data.shop_vip && (
                                <>
                                    <MetricCard
                                        title="VIP через магазин (всего)"
                                        value={fmt(data.shop_vip.total_purchases)}
                                        hint={`${fmt(data.shop_vip.unique_users)} польз.`}
                                        tone="purple"
                                    />
                                    <MetricCard
                                        title={`Магазин (${data.shop_vip.current_month.label})`}
                                        value={fmt(
                                            data.shop_vip.current_month.purchases
                                        )}
                                        hint={`сумма ${fmt(
                                            data.shop_vip.current_month.amount_sum
                                        )}`}
                                        tone="orange"
                                    />
                                </>
                            )}
                            </div>
                        </div>
                    ) : null}

                    {filteredCohorts.length > 0 && (
                        <div>
                            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Когорты игры: регистрации и VIP среди
                                зарегистрированных в этом месяце
                            </h3>
                            <CohortCharts
                                data={filteredCohorts}
                                showRegs={showRegs}
                                showVipActive={showVipActive}
                                showVipDropped={showVipDropped}
                                showFirstVip={showFirstVip}
                                showConversion={showConversion}
                            />
                        </div>
                    )}
                </div>
            )}

            {tab === "month" && hasFullData && data && (
                <div className="space-y-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Платежи VIP за текущий месяц: первые покупки и продления.
                        Отдельно — кто зарегистрировался из игры и купил VIP в
                        том же месяце.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <MetricCard
                            title={`Первые VIP (${data.current_month.label})`}
                            value={fmt(data.current_month.first_purchases)}
                            hint="первая оплата VIP"
                            tone="green"
                        />
                        <MetricCard
                            title={`Продления (${data.current_month.label})`}
                            value={fmt(data.current_month.renewals)}
                            hint="повторные оплаты"
                            tone="purple"
                        />
                        <MetricCard
                            title="Всего VIP-платежей"
                            value={fmt(data.current_month.total_payments)}
                            tone="blue"
                        />
                        <MetricCard
                            title="Рег. + VIP в этом месяце"
                            value={fmt(
                                data.new_users_same_month.first_vip_same_month
                            )}
                            hint={`из ${fmt(
                                data.new_users_same_month.registrations
                            )} регистраций → ${pct(
                                data.new_users_same_month.conversion_pct
                            )}`}
                            tone="orange"
                        />
                    </div>
                    <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-gray-500 dark:text-gray-400">
                                <tr>
                                    <th className="px-4 py-2">Метрика</th>
                                    <th className="px-4 py-2">Значение</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 text-gray-800 dark:text-gray-100">
                                <tr>
                                    <td className="px-4 py-2">
                                        Зарегистрировались раньше чем 3 мес.
                                        назад, первый VIP в{" "}
                                        {
                                            data.deferred_first_purchase
                                                .month_label
                                        }
                                    </td>
                                    <td className="px-4 py-2 font-semibold">
                                        {fmt(
                                            data.deferred_first_purchase
                                                .registered_over_3_months_first_vip_this_month
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="px-4 py-2">
                                        Зарегистрировались раньше чем 6 мес.
                                        назад, первый VIP в{" "}
                                        {
                                            data.deferred_first_purchase
                                                .month_label
                                        }
                                    </td>
                                    <td className="px-4 py-2 font-semibold">
                                        {fmt(
                                            data.deferred_first_purchase
                                                .registered_over_6_months_first_vip_this_month
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === "shop" && hasFullData && data && (
                <div className="space-y-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Покупки VIP через магазин в клиенте. Оплаты со страницы
                        VIP сюда не входят.
                    </p>
                    {data.shop_vip ? (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                <MetricCard
                                    title="Покупок через магазин (всего)"
                                    value={fmt(data.shop_vip.total_purchases)}
                                    tone="purple"
                                />
                                <MetricCard
                                    title="Уникальных пользователей"
                                    value={fmt(data.shop_vip.unique_users)}
                                    tone="blue"
                                />
                                <MetricCard
                                    title={`Этот месяц (${data.shop_vip.current_month.label})`}
                                    value={fmt(
                                        data.shop_vip.current_month.purchases
                                    )}
                                    hint={`${fmt(
                                        data.shop_vip.current_month.unique_users
                                    )} польз.`}
                                    tone="green"
                                />
                                <MetricCard
                                    title="Сумма покупок"
                                    value={fmt(data.shop_vip.amount_sum)}
                                    hint="по сумме из заказа магазина"
                                    tone="orange"
                                />
                            </div>
                            <ResponsiveContainer width="100%" height={240}>
                                <ComposedChart
                                    data={data.shop_vip.by_month.filter(
                                        (r) =>
                                            !hideEmptyMonths ||
                                            r.purchases > 0
                                    ).slice(
                                        monthsWindow < 12
                                            ? -monthsWindow
                                            : undefined
                                    )}
                                >
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke="rgba(156,163,175,0.3)"
                                    />
                                    <XAxis
                                        dataKey="label"
                                        tick={{ fontSize: 11, fill: "#9ca3af" }}
                                    />
                                    <YAxis
                                        allowDecimals={false}
                                        tick={{ fontSize: 11, fill: "#9ca3af" }}
                                    />
                                    <Tooltip content={ChartTooltipContent} />
                                    <Bar
                                        dataKey="purchases"
                                        name="Покупки в магазине"
                                        fill="#7c3aed"
                                        radius={[3, 3, 0, 0]}
                                    />
                                    <Bar
                                        dataKey="unique_users"
                                        name="Уникальные пользователи"
                                        fill="#2563eb"
                                        radius={[3, 3, 0, 0]}
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                            <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-gray-500 dark:text-gray-400">
                                        <tr>
                                            <th className="px-3 py-2">Месяц</th>
                                            <th className="px-3 py-2">
                                                Покупки
                                            </th>
                                            <th className="px-3 py-2">
                                                Пользователи
                                            </th>
                                            <th className="px-3 py-2">
                                                Сумма
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 text-gray-800 dark:text-gray-100">
                                        {data.shop_vip.by_month
                                            .filter(
                                                (r) =>
                                                    !hideEmptyMonths ||
                                                    r.purchases > 0
                                            )
                                            .map((r) => (
                                                <tr key={r.label}>
                                                    <td className="px-3 py-2 font-medium">
                                                        {r.label}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        {fmt(r.purchases)}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        {fmt(r.unique_users)}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        {fmt(r.amount_sum)}
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    ) : (
                        <div className="text-sm text-gray-500">
                            Нет покупок VIP через магазин
                        </div>
                    )}
                </div>
            )}

            {tab === "cohorts" && (
                <div className="space-y-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Только игроки. «VIP сейчас» — сколько из
                        зарегистрированных в этом месяце сейчас имеют активный
                        платный VIP (это не общее число VIP в структуре).
                    </p>
                    <CohortCharts
                        data={filteredCohorts}
                        showRegs={showRegs}
                        showVipActive={showVipActive}
                        showVipDropped={showVipDropped}
                        showFirstVip={showFirstVip}
                        showConversion={showConversion}
                    />
                    <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-gray-500 dark:text-gray-400">
                                <tr>
                                    <th className="px-3 py-2">Месяц</th>
                                    <th className="px-3 py-2">Рег.</th>
                                    <th className="px-3 py-2">VIP сейчас</th>
                                    <th className="px-3 py-2">Просрочен</th>
                                    <th className="px-3 py-2">Купили когда-либо</th>
                                    <th className="px-3 py-2">VIP в мес. рег.</th>
                                    <th className="px-3 py-2">Конверсия</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 text-gray-800 dark:text-gray-100">
                                {filteredCohorts.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            className="px-3 py-4 text-gray-500"
                                        >
                                            Нет строк под текущие фильтры
                                        </td>
                                    </tr>
                                ) : (
                                    filteredCohorts.map((c) => (
                                        <tr key={c.label}>
                                            <td className="px-3 py-2 font-medium">
                                                {c.label}
                                            </td>
                                            <td className="px-3 py-2">
                                                {fmt(c.registrations)}
                                            </td>
                                            <td className="px-3 py-2 text-green-600 dark:text-green-400">
                                                {fmt(c.vip_active)}
                                            </td>
                                            <td className="px-3 py-2 text-orange-600 dark:text-orange-400">
                                                {fmt(c.vip_dropped)}
                                            </td>
                                            <td className="px-3 py-2">
                                                {fmt(c.bought_vip_ever)}
                                            </td>
                                            <td className="px-3 py-2">
                                                {fmt(c.first_vip_same_month)}
                                            </td>
                                            <td className="px-3 py-2 font-semibold">
                                                {pct(c.conversion)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === "retention" && hasFullData && data && (
                <div className="space-y-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Доля игроков, которые после первой оплаты VIP заплатили
                        снова в следующем месяце (и ещё через месяц — для
                        «3 месяца подряд»).
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-3">
                        <MetricCard
                            title="Удержание на 2-й месяц"
                            value={pct(data.retention.retention_m2_pct)}
                            hint={`${fmt(data.retention.retained_m2)} из ${fmt(
                                data.retention.eligible_m2
                            )}`}
                            tone="green"
                        />
                        <MetricCard
                            title="Удержание 3 месяца подряд"
                            value={pct(data.retention.retention_m3_pct)}
                            hint={`${fmt(data.retention.retained_m3)} из ${fmt(
                                data.retention.eligible_m3
                            )}`}
                            tone="purple"
                        />
                    </div>
                    <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-gray-500 dark:text-gray-400">
                                <tr>
                                    <th className="px-4 py-2">Показатель</th>
                                    <th className="px-4 py-2">Могли продлить</th>
                                    <th className="px-4 py-2">Продлили</th>
                                    <th className="px-4 py-2">%</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 text-gray-800 dark:text-gray-100">
                                <tr>
                                    <td className="px-4 py-2">
                                        Оплата в месяце после первой
                                    </td>
                                    <td className="px-4 py-2">
                                        {fmt(data.retention.eligible_m2)}
                                    </td>
                                    <td className="px-4 py-2">
                                        {fmt(data.retention.retained_m2)}
                                    </td>
                                    <td className="px-4 py-2 font-semibold">
                                        {pct(data.retention.retention_m2_pct)}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="px-4 py-2">
                                        Оплаты в двух следующих месяцах подряд
                                    </td>
                                    <td className="px-4 py-2">
                                        {fmt(data.retention.eligible_m3)}
                                    </td>
                                    <td className="px-4 py-2">
                                        {fmt(data.retention.retained_m3)}
                                    </td>
                                    <td className="px-4 py-2 font-semibold">
                                        {pct(data.retention.retention_m3_pct)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === "distribution" && hasFullData && data && (
                <div className="space-y-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Сколько игроков сделали 1, 2 или 3+ оплаты VIP. Большая
                        доля с одной оплатой — отток после первого периода.
                    </p>
                    {renewalChart.every((r) => r.users === 0) ? (
                        <div className="rounded-md border border-dashed border-gray-300 dark:border-gray-600 px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                            Нет данных по продлениям
                        </div>
                    ) : (
                        <RenewalChart data={renewalChart} />
                    )}
                    <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-gray-500 dark:text-gray-400">
                                <tr>
                                    <th className="px-4 py-2">Число оплат</th>
                                    <th className="px-4 py-2">Пользователей</th>
                                    <th className="px-4 py-2">Доля</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 text-gray-800 dark:text-gray-100">
                                {(() => {
                                    const total =
                                        data.renewal_distribution.payments_1 +
                                        data.renewal_distribution.payments_2 +
                                        data.renewal_distribution
                                            .payments_3_plus;
                                    return [
                                        {
                                            label: "1 (без продления)",
                                            n: data.renewal_distribution
                                                .payments_1,
                                        },
                                        {
                                            label: "2",
                                            n: data.renewal_distribution
                                                .payments_2,
                                        },
                                        {
                                            label: "3+",
                                            n: data.renewal_distribution
                                                .payments_3_plus,
                                        },
                                    ].map((r) => (
                                        <tr key={r.label}>
                                            <td className="px-4 py-2">
                                                {r.label}
                                            </td>
                                            <td className="px-4 py-2 font-semibold">
                                                {fmt(r.n)}
                                            </td>
                                            <td className="px-4 py-2">
                                                {total > 0
                                                    ? pct(
                                                          Math.round(
                                                              (r.n * 10000) /
                                                                  total
                                                          ) / 100
                                                      )
                                                    : "—"}
                                            </td>
                                        </tr>
                                    ));
                                })()}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

const RenewalChart: React.FC<{
    data: Array<{ name: string; users: number }>;
}> = ({ data }) => {
    const { position, onMouseMove, onMouseLeave } = useCursorTooltipPosition();
    const maxUsers = Math.max(1, ...data.map((d) => d.users));

    return (
        <div
            className="h-[148px] w-full"
            role="img"
            aria-label="Распределение пользователей по числу VIP-оплат"
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
        >
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={data}
                    layout="vertical"
                    margin={{ top: 4, right: 48, left: 8, bottom: 4 }}
                >
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={SERIES.grid}
                        horizontal={false}
                    />
                    <XAxis
                        type="number"
                        domain={[0, Math.ceil(maxUsers * 1.15)]}
                        tick={{ fontSize: 11, fill: SERIES.tick }}
                        axisLine={false}
                        tickLine={false}
                        allowDecimals={false}
                    />
                    <YAxis
                        type="category"
                        dataKey="name"
                        width={72}
                        tick={{ fontSize: 11, fill: SERIES.tick }}
                        axisLine={false}
                        tickLine={false}
                    />
                    <Tooltip
                        {...chartTooltipSharedProps}
                        position={position}
                        cursor={{ fill: "rgba(30, 64, 175, 0.06)" }}
                    />
                    <Bar
                        dataKey="users"
                        name="Пользователи"
                        fill={SERIES.renewal}
                        radius={[0, 4, 4, 0]}
                        maxBarSize={22}
                        isAnimationActive={false}
                    >
                        <LabelList
                            dataKey="users"
                            position="right"
                            style={{
                                fontSize: 11,
                                fontWeight: 600,
                                fill: "#334155",
                            }}
                            formatter={(v) =>
                                fmt(typeof v === "number" ? v : Number(v))
                            }
                        />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

const StructureMonthlyChart: React.FC<{
    data: Array<{
        label: string;
        paid_covering_month_end: number;
        payments: number;
        unique_payers: number;
    }>;
}> = ({ data }) => {
    const { position, onMouseMove, onMouseLeave } = useCursorTooltipPosition();

    return (
        <div
            className="h-[260px] w-full"
            role="img"
            aria-label="VIP в структуре по месяцам"
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
        >
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                    data={data}
                    margin={{ top: 16, right: 16, left: 0, bottom: 4 }}
                >
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={SERIES.grid}
                        vertical={false}
                    />
                    <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11, fill: SERIES.tick }}
                    />
                    <YAxis
                        yAxisId="vip"
                        tick={{ fontSize: 11, fill: SERIES.tick }}
                        allowDecimals={false}
                    />
                    <YAxis
                        yAxisId="pay"
                        orientation="right"
                        tick={{ fontSize: 11, fill: SERIES.tick }}
                        allowDecimals={false}
                    />
                    <Tooltip
                        {...chartTooltipSharedProps}
                        position={position}
                    />
                    <Bar
                        yAxisId="vip"
                        dataKey="paid_covering_month_end"
                        name="VIP с покрытием на конец мес."
                        fill={SERIES.structureVip}
                        radius={[4, 4, 0, 0]}
                        isAnimationActive={false}
                    >
                        <LabelList
                            dataKey="paid_covering_month_end"
                            position="top"
                            style={{
                                fontSize: 10,
                                fontWeight: 600,
                                fill: SERIES.structureVip,
                            }}
                            formatter={(v) => {
                                const n =
                                    typeof v === "number" ? v : Number(v);
                                return n > 0 ? fmt(n) : "";
                            }}
                        />
                    </Bar>
                    <Line
                        yAxisId="pay"
                        type="monotone"
                        dataKey="payments"
                        name="Платежи VIP"
                        stroke={SERIES.structurePay}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        isAnimationActive={false}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
};

const CohortCharts: React.FC<{
    data: CohortRow[];
    showRegs: boolean;
    showVipActive: boolean;
    showVipDropped: boolean;
    showFirstVip: boolean;
    showConversion: boolean;
}> = ({
    data,
    showRegs,
    showVipActive,
    showVipDropped,
    showFirstVip,
    showConversion,
}) => {
    const { position, onMouseMove, onMouseLeave } = useCursorTooltipPosition();
    const showVipAxis = showVipActive || showVipDropped || showFirstVip;

    const vipMax = Math.max(
        1,
        ...data.map((d) =>
            Math.max(
                showVipActive ? d.vip_active || 0 : 0,
                showVipDropped ? d.vip_dropped || 0 : 0,
                showFirstVip ? d.first_vip_same_month || 0 : 0
            )
        )
    );

    const pctMax = Math.max(
        1,
        ...data.map((d) => (showConversion ? d.conversion || 0 : 0))
    );

    if (data.length === 0) {
        return (
            <div className="rounded-md border border-dashed border-slate-300 dark:border-slate-600 px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                Нет строк под текущие фильтры — снимите «Скрыть пустые месяцы»
                или расширьте окно.
            </div>
        );
    }

    return (
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-700 dark:bg-slate-900/40">
            <div
                className="h-[240px] w-full"
                role="img"
                aria-label="Когорты: регистрации, VIP и конверсия по месяцам"
                onMouseMove={onMouseMove}
                onMouseLeave={onMouseLeave}
            >
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={data}
                        margin={{
                            top: 18,
                            right: showVipAxis || showConversion ? 36 : 8,
                            left: 0,
                            bottom: 0,
                        }}
                        barCategoryGap="22%"
                        barGap={2}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={SERIES.grid}
                            vertical={false}
                        />
                        <XAxis
                            dataKey="label"
                            tick={{ fontSize: 11, fill: SERIES.tick }}
                            axisLine={{ stroke: SERIES.grid }}
                            tickLine={false}
                            interval="preserveStartEnd"
                        />
                        <YAxis
                            yAxisId="left"
                            tick={{ fontSize: 10, fill: SERIES.tick }}
                            allowDecimals={false}
                            axisLine={false}
                            tickLine={false}
                            width={40}
                            hide={!showRegs}
                        />
                        {showVipAxis ? (
                            <YAxis
                                yAxisId="vip"
                                orientation="right"
                                tick={{ fontSize: 10, fill: SERIES.vipActive }}
                                allowDecimals={false}
                                domain={[0, Math.ceil(vipMax * 1.25) || 1]}
                                axisLine={false}
                                tickLine={false}
                                width={28}
                            />
                        ) : null}
                        {showConversion ? (
                            <YAxis
                                yAxisId="pct"
                                orientation="right"
                                hide={showVipAxis}
                                tick={{
                                    fontSize: 10,
                                    fill: SERIES.conversion,
                                }}
                                domain={[0, Math.ceil(pctMax * 1.25) || 1]}
                                axisLine={false}
                                tickLine={false}
                                width={32}
                                tickFormatter={(v) => `${v}%`}
                            />
                        ) : null}
                        <Tooltip
                            {...chartTooltipSharedProps}
                            position={position}
                        />
                        {showRegs ? (
                            <Area
                                yAxisId="left"
                                type="monotone"
                                dataKey="registrations"
                                name="Регистрации"
                                stroke={SERIES.regs}
                                fill={SERIES.regsFill}
                                strokeWidth={1.5}
                                isAnimationActive={false}
                                dot={false}
                                activeDot={{ r: 3 }}
                            />
                        ) : null}
                        {showVipActive ? (
                            <Bar
                                yAxisId="vip"
                                dataKey="vip_active"
                                name="VIP сейчас (из когорты)"
                                fill={SERIES.vipActive}
                                radius={[3, 3, 0, 0]}
                                maxBarSize={28}
                                isAnimationActive={false}
                            >
                                <LabelList
                                    dataKey="vip_active"
                                    position="top"
                                    style={{
                                        fontSize: 10,
                                        fontWeight: 600,
                                        fill: SERIES.vipActive,
                                    }}
                                    formatter={(v) => {
                                        const n =
                                            typeof v === "number"
                                                ? v
                                                : Number(v);
                                        return n > 0 ? String(n) : "";
                                    }}
                                />
                            </Bar>
                        ) : null}
                        {showVipDropped ? (
                            <Bar
                                yAxisId="vip"
                                dataKey="vip_dropped"
                                name="VIP просрочен (из когорты)"
                                fill={SERIES.vipDropped}
                                radius={[3, 3, 0, 0]}
                                maxBarSize={28}
                                isAnimationActive={false}
                            />
                        ) : null}
                        {showFirstVip ? (
                            <Bar
                                yAxisId="vip"
                                dataKey="first_vip_same_month"
                                name="VIP в мес. рег."
                                fill={SERIES.firstVip}
                                radius={[3, 3, 0, 0]}
                                maxBarSize={28}
                                isAnimationActive={false}
                            />
                        ) : null}
                        {showConversion ? (
                            <Line
                                yAxisId="pct"
                                type="monotone"
                                dataKey="conversion"
                                name="Конверсия %"
                                stroke={SERIES.conversion}
                                strokeWidth={2}
                                strokeDasharray="4 3"
                                dot={{
                                    r: 3.5,
                                    fill: SERIES.conversion,
                                    strokeWidth: 0,
                                }}
                                activeDot={{ r: 5 }}
                                isAnimationActive={false}
                            >
                                {!showVipAxis ? (
                                    <LabelList
                                        dataKey="conversion"
                                        position="top"
                                        style={{
                                            fontSize: 10,
                                            fontWeight: 600,
                                            fill: SERIES.conversion,
                                        }}
                                        formatter={(v) => {
                                            const n =
                                                typeof v === "number"
                                                    ? v
                                                    : Number(v);
                                            return Number.isFinite(n) && n > 0
                                                ? `${n}%`
                                                : "";
                                        }}
                                    />
                                ) : null}
                            </Line>
                        ) : null}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
            <p className="mt-2 text-[11px] leading-snug text-slate-500 dark:text-slate-400">
                {showVipAxis
                    ? "Справа — число VIP. Конверсия % — пунктир (точные значения при наведении)."
                    : showConversion
                      ? "Справа — конверсия %."
                      : "Включите нужные серии кнопками выше."}
            </p>
        </div>
    );
};
